import type { ActionFunctionArgs } from "react-router-dom";
import { organizationAPI, APIError } from "../../lib/api";
import { createApiKeySchema, updateApiKeySchema } from "../../schemas";
import type { ActionResult } from "./auth.actions";

export async function createApiKeyAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id } = params;

  if (!id) {
    return { success: false, error: "Organization ID is required" };
  }

  const formData = await request.formData();
  const name = formData.get("name") as string;
  const scopes = formData.getAll("scopes") as string[];

  const result = createApiKeySchema.safeParse({ name, scopes });

  if (!result.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const field = issue.path[0];
      if (typeof field === "string") {
        fieldErrors[field] = issue.message;
      }
    }
    return { success: false, fieldErrors };
  }

  try {
    const response = await organizationAPI.createApiKey(id, result.data);
    return { success: true, data: response };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateApiKeyAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id, keyId } = params;

  if (!id || !keyId) {
    return { success: false, error: "Organization ID and Key ID are required" };
  }

  const formData = await request.formData();
  const name = formData.get("name") as string | undefined;
  const scopes = formData.getAll("scopes") as string[];

  const data: Record<string, unknown> = {};
  if (name) data.name = name;
  if (scopes.length > 0) data.scopes = scopes;

  const result = updateApiKeySchema.safeParse(data);

  if (!result.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const field = issue.path[0];
      if (typeof field === "string") {
        fieldErrors[field] = issue.message;
      }
    }
    return { success: false, fieldErrors };
  }

  try {
    const apiKey = await organizationAPI.updateApiKey(id, keyId, result.data);
    return { success: true, data: apiKey };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function revokeApiKeyAction({ params }: ActionFunctionArgs): Promise<ActionResult> {
  const { id, keyId } = params;

  if (!id || !keyId) {
    return { success: false, error: "Organization ID and Key ID are required" };
  }

  try {
    const apiKey = await organizationAPI.revokeApiKey(id, keyId);
    return { success: true, data: apiKey };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function rotateApiKeyAction({ params }: ActionFunctionArgs): Promise<ActionResult> {
  const { id, keyId } = params;

  if (!id || !keyId) {
    return { success: false, error: "Organization ID and Key ID are required" };
  }

  try {
    const response = await organizationAPI.rotateApiKey(id, keyId);
    return { success: true, data: response };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
